// ============================================================
//  GIFTING LOVES — SITE CONTENT
//  Edit everything here: brand, contact details, products,
//  prices, images, reviews. No other file needs to change.
// ============================================================

import {
  Gift,
  Heart,
  MessageCircle,
  Package,
  PenLine,
} from "lucide-react";

export const BRAND = {
  name: "Gifting Loves",
  tagline: "Thoughtful Gifts. Beautiful Memories.",
};

// ---- BUSINESS CONTACT DETAILS (edit here) ----
export const CONTACT = {
  whatsapp: "919080879030", // country code + number, digits only
  phoneDisplay: "+91 90808 79030",
  email: "hello@giftingloves.com",
  instagram: "gifting_loves",
  instagramUrl: "https://instagram.com/gifting_loves",
  facebook: "Gifting Loves",
  facebookUrl: "https://www.facebook.com/giftingloves",
  address:
    "424/1, 424, Arcot Rd, opp. Uzahavar Sandhai, Kagithapatarai, Vellore, Tamil Nadu 632012",
  mapsUrl:
    "https://www.google.com/maps/search/?api=1&query=" +
    encodeURIComponent(
      "424/1, 424, Arcot Rd, opp. Uzahavar Sandhai, Kagithapatarai, Vellore, Tamil Nadu 632012",
    ),
  paymentNote: "Online payment only · No COD",
};

export const waLink = (message) =>
  `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(message)}`;

export const NAV_LINKS = [
  { label: "Home", hash: "#home" },
  { label: "Shop", hash: "#shop" },
  { label: "Categories", hash: "#categories" },
  { label: "About", hash: "#about" },
  { label: "Contact", hash: "#contact" },
];

const img = (id, w = 900) =>
  `https://images.unsplash.com/photo-${id}?q=80&w=${w}&auto=format&fit=crop`;

export const HERO_IMAGE = img("1513201099705-a9746e1e201f", 1200);
export const MANIFESTO_IMAGE = img("1574858818846-886b8380713b", 1200);

// ---- CATEGORIES ----
export const CATEGORIES = [
  {
    id: "birthday",
    name: "Birthday Gifts",
    tagline: "Candles, confetti & surprises",
    image: img("1464349095431-e9a21285b5f3"),
    alt: "Birthday cake with a lit sparkler and candles",
  },
  {
    id: "anniversary",
    name: "Anniversary Gifts",
    tagline: "Celebrate your forever",
    image: img("1521478706270-f2e33c203d95"),
    alt: "Romantic anniversary gift with roses",
  },
  {
    id: "wedding",
    name: "Wedding Gifts",
    tagline: "For their big day",
    image: img("1519741497674-611481863552"),
    alt: "Elegant wedding celebration moment",
  },
  {
    id: "personalized",
    name: "Personalized Gifts",
    tagline: "Made just for them",
    image: img("1512909006721-3d6018887383"),
    alt: "Hands wrapping a personalized gift in kraft paper",
  },
  {
    id: "couple",
    name: "Couple Gifts",
    tagline: "Two hearts, one box",
    image: img("1606800052052-a08af7148866"),
    alt: "A couple sharing a warm moment together",
  },
  {
    id: "baby",
    name: "Baby Gifts",
    tagline: "Welcome, little one",
    image: img("1704988935392-09fc355154aa"),
    alt: "Soft baby shower gifts and toys",
  },
  {
    id: "corporate",
    name: "Corporate Gifts",
    tagline: "Impress clients & teams",
    image: img("1638295916768-459f6cf440bc"),
    alt: "Minimal elegant corporate gift box",
  },
  {
    id: "hampers",
    name: "Hampers",
    tagline: "Overflowing with joy",
    image: img("1635952346904-95f2ccfcd029"),
    alt: "Festive Indian sweets hamper",
  },
];

// ---- FEATURED PRODUCTS ----
export const PRODUCTS = [
  {
    id: "personalized-gift-hamper",
    name: "Personalized Gift Hamper",
    category: "personalized",
    price: "₹1,999",
    description:
      "A curated hamper with custom-name keepsakes, artisan treats and a handwritten note.",
    longDescription:
      "Tell us who it is for and we will build the hamper around them — their name, their favourites, your words.",
    details: [
      "Name-personalized keepsake included",
      "Handwritten message card",
      "Premium rose-gold packaging",
      "Delivered across India",
    ],
    image: img("1549465220-1a8b9238cd48"),
    alt: "Beautifully wrapped personalized gift boxes with ribbons",
  },
  {
    id: "couple-memory-box",
    name: "Couple Memory Box",
    category: "couple",
    price: "₹1,499",
    description:
      "A keepsake box for tickets, photos, letters and the little moments you have shared.",
    longDescription:
      "Add your dates, inside jokes and photographs — we turn them into a box you will open for years.",
    details: [
      "Custom photo prints included",
      "Engraved wooden keepsake box",
      "52 date-night idea cards",
      "Handwritten message card",
    ],
    image: img("1606800052052-a08af7148866"),
    alt: "A couple in warm light sharing a quiet moment",
  },
  {
    id: "birthday-surprise-box",
    name: "Birthday Surprise Box",
    category: "birthday",
    price: "₹999",
    description:
      "Confetti, candles, sweet treats and a pop of joy — ready to make their day.",
    longDescription:
      "Open the lid and the party begins: balloons, treats and a card written in your words.",
    details: [
      "Pop-up confetti surprise",
      "Assorted sweet treats",
      "Personalized birthday card",
      "Same-week delivery slots",
    ],
    image: img("1530103862676-de8c9debad1d"),
    alt: "Colourful birthday party balloons and celebration decor",
  },
  {
    id: "chocolate-flower-hamper",
    name: "Chocolate & Flower Hamper",
    category: "hampers",
    price: "₹1,799",
    description:
      "Fresh blooms paired with premium chocolates in our signature rose packaging.",
    longDescription:
      "A classic for a reason — seasonal flowers and hand-picked chocolates, arranged the morning it ships.",
    details: [
      "Fresh seasonal flowers",
      "Premium assorted chocolates",
      "Signature rose packaging",
      "Care card included",
    ],
    image: img("1599791095997-5cf38bb5ff69"),
    alt: "A premium bouquet of fresh flowers",
  },
  {
    id: "customized-photo-frame",
    name: "Customized Photo Frame",
    category: "personalized",
    price: "₹799",
    description:
      "Your favourite memory, printed in gallery quality and framed with your message.",
    longDescription:
      "Send us the photo and the words — we handle the print, the frame and the finishing touches.",
    details: [
      "Gallery-quality photo print",
      "Choice of frame finishes",
      "Custom engraved message",
      "Ready to hang or stand",
    ],
    image: img("1513519245088-0e12902e5a38"),
    alt: "An elegant photo frame styled on a wall",
  },
  {
    id: "baby-welcome-hamper",
    name: "Baby Welcome Hamper",
    category: "baby",
    price: "₹1,299",
    description:
      "Soft essentials, a cuddly friend and a welcome card for the newest arrival.",
    longDescription:
      "Gentle, safe and adorable — curated with new parents in mind and wrapped in pastels.",
    details: [
      "Organic cotton essentials",
      "Plush cuddly toy",
      "Personalized welcome card",
      "Pastel keepsake packaging",
    ],
    image: img("1522771930-78848d9293e8"),
    alt: "Soft baby essentials arranged as a welcome gift",
  },
  {
    id: "anniversary-gift-box",
    name: "Anniversary Gift Box",
    category: "anniversary",
    price: "₹1,699",
    description:
      "A romantic curation of candles, keepsakes and sweets for your milestone.",
    longDescription:
      "Whether it is year one or year fifty, this box says it softly — scented candles, keepsakes and something sweet.",
    details: [
      "Hand-poured scented candle",
      "Anniversary keepsake token",
      "Gourmet sweets selection",
      "Handwritten message card",
    ],
    image: img("1522673607200-164d1b6ce486"),
    alt: "Wedding rings resting on soft fabric",
  },
  {
    id: "self-care-gift-hamper",
    name: "Self-Care Gift Hamper",
    category: "hampers",
    price: "₹1,199",
    description:
      "Bath salts, candles, tea and calm — a gentle reminder to slow down.",
    longDescription:
      "For the friend who never stops. Everything inside is chosen to give them one quiet, golden hour.",
    details: [
      "Aromatherapy bath salts",
      "Soy wax candle",
      "Herbal tea selection",
      "Linen eye mask",
    ],
    image: img("1608571423902-eed4a5ad8108"),
    alt: "Self-care skincare products arranged in soft light",
  },
];

// ---- OCCASIONS ----
export const OCCASIONS = [
  { name: "Birthday" },
  { name: "Anniversary" },
  { name: "Wedding" },
  { name: "Baby Shower" },
  { name: "Friendship" },
  { name: "Festivals" },
  { name: "Just Because" },
];

// ---- WHY CHOOSE US ----
export const BENEFITS = [
  {
    icon: Gift,
    title: "Thoughtfully Curated",
    description:
      "Every hamper is hand-assembled around the person receiving it — never pulled off a shelf.",
  },
  {
    icon: PenLine,
    title: "Personalized Options",
    description:
      "Names, photos, dates and messages, woven into gifts that could belong to no one else.",
  },
  {
    icon: Package,
    title: "Beautiful Packaging",
    description:
      "Signature rose boxes, silk ribbons and wax seals. The unboxing is half the gift.",
  },
  {
    icon: Heart,
    title: "Made With Love",
    description:
      "A small studio, not a warehouse. Every order is checked, wrapped and sealed by hand.",
  },
  {
    icon: MessageCircle,
    title: "Easy Ordering",
    description:
      "No accounts, no carts, no checkout maze. Enquire on WhatsApp and we handle the rest.",
  },
];

// ---- TESTIMONIALS (editable) ----
export const TESTIMONIALS = [
  {
    name: "Ananya Sharma",
    city: "Mumbai",
    context: "Birthday Hamper",
    rating: 5,
    quote:
      "The hamper made my sister cry happy tears. The handwritten card and packaging felt so personal — nothing like ordering online.",
  },
  {
    name: "Rohan Mehta",
    city: "Delhi",
    context: "Anniversary Box",
    rating: 5,
    quote:
      "Ordered an anniversary box over WhatsApp in ten minutes. It arrived wrapped like something from a boutique in Paris.",
  },
  {
    name: "Priya Nair",
    city: "Bengaluru",
    context: "Corporate Gifts",
    rating: 5,
    quote:
      "We sent 40 personalized hampers to our team. Every single one had the right name, the right note. Flawless.",
  },
  {
    name: "Kavya & Arjun",
    city: "Pune",
    context: "Couple Memory Box",
    rating: 5,
    quote:
      "They printed our photos, engraved our date, even hid our inside joke on a card. It felt like they knew us.",
  },
];

// ---- INSTAGRAM GALLERY (placeholder tiles) ----
export const INSTAGRAM_IMAGES = [
  {
    src: img("1763713512973-ed285caa8ba1", 700),
    alt: "A couple in warm golden light",
  },
  {
    src: img("1544787219-7f47ccb76574", 700),
    alt: "A calming cup of tea, part of a self-care hamper",
  },
  {
    src: img("1558961363-fa8fdf82db35", 700),
    alt: "An elegant dessert table at a celebration",
  },
  {
    src: img("1761211115639-54394f139142", 700),
    alt: "Personalized engraved jewelry gift",
  },
];

// ---- ENQUIRY FORM OPTIONS ----
export const OCCASION_OPTIONS = [
  "Birthday",
  "Anniversary",
  "Wedding",
  "Baby Shower",
  "Festivals",
  "Corporate",
  "Just Because",
];

export const BUDGET_OPTIONS = [
  "Under ₹1,000",
  "₹1,000 – ₹2,500",
  "₹2,500 – ₹5,000",
  "₹5,000+",
];
